/**
 * Error Handling Utilities
 * Provides standardized error handling for the application
 * Implements the ApiError pattern specified in the guardrails
 */
import { PostgrestError } from '@supabase/supabase-js';
import React from 'react';

/**
 * API Error Class
 * Custom error class for API errors with status code
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  
  constructor(message: string, status: number = 500, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    
    // Ensures proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Type predicate for ApiError
 * Allows for type narrowing in error handling
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Type guard for PostgrestError
 */
export function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object' && 
    error !== null && 
    'code' in error && 
    'details' in error && 
    'hint' in error && 
    'message' in error
  );
}

/**
 * Type guard for Supabase Storage Error
 */
export function isStorageError(error: unknown): error is Error {
  return (
    error instanceof Error && 
    typeof error.message === 'string' && 
    error.message.includes('storage')
  );
}

/**
 * Map any Supabase error to ApiError
 * Works with both PostgrestError and Storage errors
 */
export function handleSupabaseError(error: unknown): ApiError {
  // Map common Supabase error codes to appropriate HTTP status codes
  let status = 500;
  let errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
  let errorCode = 'UNKNOWN_ERROR';
  
  // Handle PostgrestError with code property
  if (isPostgrestError(error)) {
    errorCode = error.code || 'POSTGREST_ERROR';
    
    // PostgreSQL error codes
    if (error.code === '23503') {
      // Foreign key constraint violation
      status = 400;
      errorMessage = `Foreign key constraint violation: ${error.details || errorMessage}`;
    } else if (error.code === '23505') {
      // Unique constraint violation
      status = 400;
      errorMessage = `Duplicate entry: ${error.details || errorMessage}`;
    } else if (error.code === '23502') {
      // Not null constraint violation
      status = 400;
      errorMessage = `Required field missing: ${error.details || errorMessage}`;
    } else if (error.code === '22P02') {
      // Invalid text representation
      status = 400;
      errorMessage = `Invalid input format: ${error.details || errorMessage}`;
    }
    // PGRST* codes from PostgREST
    else if (error.code === 'PGRST116') {
      // Resource not found
      status = 404;
    } else if (typeof error.code === 'string' && error.code.startsWith('PGRST1')) {
      // Bad request
      status = 400;
    }
    
    // Log detailed error information for debugging
    
    
    return new ApiError(errorMessage, status, errorCode);
  }
  
  // Handle Storage errors
  if (isStorageError(error)) {
    errorCode = 'STORAGE_ERROR';
    
    if (errorMessage.includes('not found')) {
      status = 404;
    } else if (errorMessage.includes('permission') || errorMessage.includes('access')) {
      status = 403;
    } else if (errorMessage.includes('limit') || errorMessage.includes('size')) {
      status = 413; // Payload Too Large
    }
    
    return new ApiError(errorMessage, status, errorCode);
  }
  
  // Handle errors that don't match specific types
  return new ApiError(
    errorMessage, 
    status, 
    errorCode
  );
}

/**
 * Handle PostgreSQL constraint violations
 * Internal helper for handleSupabaseError
  if (error.code?.startsWith('23')) {
    status = 400;
  }
  
  // 42* codes are PostgreSQL syntax or permission errors
  if (error.code?.startsWith('42')) {
    status = 403;
  }
  
  return new ApiError(
    error.message || 'An unexpected database error occurred',
    status,
    error.code
  );
}

/**
 * Map Auth error to ApiError
 */
export function handleAuthError(error: Error & { status?: number }): ApiError {
  return new ApiError(
    error.message || 'Authentication error',
    error.status || 401
  );
}

/**
 * Safely handle unknown errors
 */
export function handleUnknownError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }
  
  if (error instanceof Error) {
    return new ApiError(error.message);
  }
  
  return new ApiError(
    typeof error === 'string' ? error : 'An unknown error occurred'
  );
}

/**
 * Format error message for display
 * Sanitizes error messages for user presentation
 */
export function formatErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
}

/**
 * Create an error boundary component
 * Can be used as a wrapper component to catch errors
 */
export function createErrorBoundary(fallback: React.ReactNode) {
  return class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; error: Error | null }
  > {
    constructor(props: { children: React.ReactNode }) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
      return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {

    }

    render() {
      if (this.state.hasError) {
        return fallback;
      }

      return this.props.children;
    }
  };
}
